import type { GridStackWidget } from 'gridstack';
import type { DashboardWidgetContent } from '../Components/Dashboard/dashboardTypes';
import type { DriveUnitPortOccupancy } from '../redux/ApplicationSlice';
import {
  BOOLEAN_WIDGET_TYPE,
  CUSTOM_CALLABLE_WIDGET_TYPE,
  isElectronicSensorStreamSensorKey,
  isMotorStreamSensorKey,
  isOtherElectronicsStreamSensorKey,
  MECHANISM_WIDGET_TYPE,
  MOTOR_MULTI_DEFAULT_METRIC,
  MOTOR_MULTI_WIDGET_TYPE,
  MOTOR_WIDGET_TYPE,
  OTHER_WIDGET_TYPE,
  isMotorMultiWidgetDataKey,
  motorStreamKeysForPortAndMetrics,
  parseMotorMultiWidgetDataKey,
  SENSOR_OBJECTS,
  SENSOR_WIDGET_TYPE,
  drivebaseWidgetStreamDataKey,
  streamMetricSlug,
  sensorClassNameFromStreamKey,
  streamPortFromSensorKey,
  otherClassNameFromStreamKey,
} from '../Components/Graphs/sensor-utils';
import {
  motorStreamKeysForPorts,
  motorStreamKeysForProjectPortAndMetrics,
  otherStreamKeysForPorts,
  buildMechanismDrawerPortLabelByPort,
  buildMechanismVariableNameByPort,
  buildMotorChartSeriesLabelByPort,
  buildMotorVariableNameByPort,
  buildOtherElectronicsChartSeriesLabelByPort,
  buildOtherElectronicsVariableNameByPort,
  buildSensorChartSeriesLabelByPort,
  buildSensorVariableNameByPort,
  getConfiguredSensorClassNames,
  motorPortsFromStreamKeys,
  otherPortsFromStreamKeys,
  type StreamProjectContext,
} from './electronicsDashboardUtils';
import { escapePythonStringLiteral } from './pythonActionFormatting';
import { formatGeneratedPythonCall } from './pythonFormattingUtils';
import {
  consumeThroughLineBreak,
  extractCallArgsText,
  findMatchingDelimiterIndex,
  getIndentAtCodeIndex,
  isOffsetInPythonStringOrLineComment,
  offsetAtCodeLineColumn,
  parseDoubleQuotedStringLiterals,
  collectModuleFlowStatements,
  containsPythonLineCommentOutsideStrings,
  stripPythonLineCommentsOutsideStrings,
  skipWhitespaceInCode,
  splitCodeLines,
} from './pythonSourceScanUtils';
import { DATA_STREAMING_REGION_START, findRegionSpanLineIndices, isDataStreamingRegionStartLine, REGION_END } from './programRegionMarkers';
import { getEffectiveRobotStartLineInCode, getInsertLineBeforeRobotStartInCode } from './robotStartCallUtils';
import { resolveCustomCallableDisplayName } from '../Components/Dashboard/Widgets/widgetTitle';
import { COUNTDOWN_TIMER_WIDGET_TYPE } from './countdownTimerCodeUtils';
import { findUpdateMonitorDataCallRanges, UPDATE_MONITOR_DATA_CALL, type UpdateMonitorDataCallRange } from './monitorDataCallUtils';

/** Maximum number of unique items in `DataStreamStore.set_request_list([...])`. */
export const MAX_SENSOR_STREAMS = 20;

/** Default `DataStreamStore.set_streaming_interval(...)` value in milliseconds when dashboard widgets require streams. */
export const DEFAULT_STREAMING_INTERVAL_MS = 150;

/** Matches `DataStreamStore.set_streaming_interval(...)` and legacy standalone `set_streaming_interval(...)`. */
const SET_STREAMING_INTERVAL_LINE_RE = /^\s*(?:DataStreamStore\.)?set_streaming_interval\s*\(\s*(\d+)\s*\)\s*$/;

/** Widget types with fixed stream keys when `dataKey` is `all` (not derived from `SENSOR_OBJECTS`). */
const STREAM_KEYS_BY_WIDGET_TYPE: Record<string, string[]> = {
  orientation: ['Locator Yaw'],
  field: ['Position X', 'Position Y', 'Locator Yaw'],
};

/** Widget types that never need `DataStreamStore.set_request_list` updates. */
const WIDGET_TYPES_WITHOUT_DATA_STREAM = new Set(['console', 'timer', COUNTDOWN_TIMER_WIDGET_TYPE]);

const SET_REQUEST_LIST_MARKER = 'set_request_list';
/**
 * Everything allowed between the line start and `set_request_list`: indentation, `DataStreamStore.`,
 * and the optional legacy `get_instance().` hop. Anchoring both ends keeps a `#` comment, a preceding
 * `;` statement, and a `MyDataStreamStore` lookalike from matching, since a match makes the whole line
 * eligible for rewrite.
 *
 * A module-qualified call (`sphero.DataStreamStore.set_request_list(...)`) is deliberately NOT matched.
 * The streaming block's other two lines are written by helpers that only emit and recognize the bare
 * receiver, so rewriting a qualified request list in place would leave that statement qualified and its
 * siblings bare — a `NameError` plus a duplicate registration.
 *
 * Skipping means the user's qualified statements are preserved verbatim, but the dashboard does not
 * sync them: it falls through to inserting its own bare block. Where that block lands — and whether it
 * runs — depends on the program's start call, not on the qualifier:
 *
 * - `sphero.RobotBase().start()` is not recognized either, so the block is appended at end of file,
 *   after the blocking start call, where it never executes.
 * - `robot = sphero.RobotBase()` with `robot.start()` IS recognized, so the block is inserted above
 *   the start call and does run — raising `NameError` under `import sphero`, or duplicating the
 *   registration when the program also star-imports.
 *
 * The second shape is unchanged from `develop` and is not made worse here, but it is the reason this
 * skip is a deferral rather than a fix. Supporting qualified programs means teaching all three helpers
 * the qualifier; see BPS-902.
 */
const SET_REQUEST_LIST_RECEIVER_PREFIX_RE = /^[ \t]*DataStreamStore[ \t]*\.[ \t]*(?:get_instance[ \t]*\([ \t]*\)[ \t]*\.[ \t]*)?$/;
/** Trailing text a matched statement may carry: whitespace (including a CR), then at most a line comment. */
const SET_REQUEST_LIST_TRAILING_RE = /^[ \t]*(#.*)?\r?$/;

type SetRequestListCallSpan = {
  start: number;
  end: number;
  listInnerStart: number;
  listInnerEnd: number;
  /** Index of `DataStreamStore` for deriving the statement's line indent. */
  calleeStart: number;
  /** Zero-based line of the statement header, for checking where the call sits in module flow. */
  startLineIndex: number;
};

/** Sorted JSON signature for comparing stream-key sets regardless of order. */
export function stableKeysSignature(keys: string[]): string {
  return JSON.stringify([...keys].sort());
}

/** Layout signature for dashboard stream code sync (union of widget stream keys). */
export function getDashboardStreamSyncLayoutSignature(widgets: GridStackWidget[]): string {
  return stableKeysSignature(collectUnionSensorKeysFromLayout(widgets));
}

/**
 * Stream keys required for a dashboard widget.
 * When `dataKey` is `all`, returns all series keys for that graph type (or widget-specific defaults).
 */
export function getStreamKeysForWidget(widgetType: string, dataKey: string): string[] {
  if (WIDGET_TYPES_WITHOUT_DATA_STREAM.has(widgetType)) return [];

  if (
    widgetType === MOTOR_WIDGET_TYPE ||
    widgetType === MOTOR_MULTI_WIDGET_TYPE ||
    widgetType === SENSOR_WIDGET_TYPE ||
    widgetType === OTHER_WIDGET_TYPE ||
    widgetType === BOOLEAN_WIDGET_TYPE
  ) {
    if (widgetType === MOTOR_MULTI_WIDGET_TYPE && dataKey && isMotorMultiWidgetDataKey(dataKey)) {
      return [];
    }
    return dataKey && dataKey !== 'all' ? [dataKey] : [];
  }

  const specialKeys = STREAM_KEYS_BY_WIDGET_TYPE[widgetType];
  if (specialKeys && dataKey === 'all') return [...specialKeys];

  if (dataKey && dataKey !== 'all') return [drivebaseWidgetStreamDataKey(widgetType, dataKey)];

  const graph = SENSOR_OBJECTS[widgetType];
  if (graph?.series?.length) {
    return graph.series.map((series) => series.dataKey);
  }

  if (specialKeys) return [...specialKeys];
  return [];
}

export function createDashboardWidgetContent(opts: {
  name: string;
  dataKey: string;
  curve?: string;
  style?: string;
  props?: object;
  options?: DashboardWidgetContent['options'];
  sensorKeys?: string[];
  streamProjectContext?: StreamProjectContext;
}): string {
  const sensorKeys = opts.sensorKeys ?? getStreamKeysForWidget(opts.name, String(opts.dataKey ?? ''));
  const draft: DashboardWidgetContent = {
    name: opts.name,
    curve: opts.curve ?? 'linear',
    style: opts.style ?? 'line',
    dataKey: opts.dataKey,
    sensorKeys,
    props: opts.props ?? {},
    ...(opts.options ? { options: opts.options } : {}),
  };
  const content: DashboardWidgetContent = {
    ...draft,
    sensorKeys: resolveWidgetSensorKeys(draft, opts.streamProjectContext),
  };
  return JSON.stringify(content);
}

/**
 * Effective stream keys for a widget. For `motor_multi`, derives keys from `selectedMotorMetrics`
 * so live ingest and `set_request_list` stay aligned with the chart even if `sensorKeys` is stale.
 */
export function resolveWidgetSensorKeys(content: DashboardWidgetContent, streamProjectContext?: StreamProjectContext): string[] {
  const { name, dataKey, sensorKeys, options } = content;

  if (name === COUNTDOWN_TIMER_WIDGET_TYPE) {
    return [];
  }

  if (name === BOOLEAN_WIDGET_TYPE && dataKey && isMotorStreamSensorKey(dataKey)) {
    const ports = options?.selectedMotorPorts;
    if (ports?.length) {
      const keys = motorStreamKeysForPorts(dataKey, ports);
      if (keys.length) return keys;
    }
    return sensorKeys;
  }

  if (name === OTHER_WIDGET_TYPE && dataKey) {
    const ports = options?.selectedOtherPorts;
    if (ports?.length) {
      const keys = otherStreamKeysForPorts(dataKey, ports);
      if (keys.length) return keys;
    }
    return sensorKeys;
  }

  if (name !== MOTOR_MULTI_WIDGET_TYPE || !isMotorMultiWidgetDataKey(dataKey)) {
    if (name === 'gyro') {
      return sensorKeys.map((key) => drivebaseWidgetStreamDataKey(name, key));
    }
    return sensorKeys;
  }

  const parsed = parseMotorMultiWidgetDataKey(dataKey);
  const port = options?.selectedMotorPort ?? parsed?.port;
  const prefix = parsed?.prefix ?? 'motor';
  const metrics = options?.selectedMotorMetrics;

  if (port != null && metrics?.length) {
    if (streamProjectContext) {
      const kind = prefix === 'mechanism' ? 'mechanism' : 'motor';
      const resolved = motorStreamKeysForProjectPortAndMetrics(streamProjectContext.portRows, streamProjectContext.projectCode, port, metrics, kind);
      if (resolved.length === metrics.length) return resolved;
    }
    const fromPersisted = metrics
      .map((metric) => sensorKeys.find((key) => streamMetricSlug(key) === metric))
      .filter((key): key is string => Boolean(key));
    if (fromPersisted.length === metrics.length) return fromPersisted;
    return motorStreamKeysForPortAndMetrics(port, prefix, metrics);
  }
  if (sensorKeys.length) return sensorKeys;
  if (port != null) {
    return motorStreamKeysForPortAndMetrics(port, prefix, [MOTOR_MULTI_DEFAULT_METRIC]);
  }
  return [];
}

export function parseDashboardWidgetContent(content: string | undefined): DashboardWidgetContent | null {
  if (!content) return null;
  try {
    const parsed = JSON.parse(content) as Partial<DashboardWidgetContent>;
    if (!parsed.name) return null;
    const dataKey = String(parsed.dataKey ?? '');
    const storedSensorKeys = Array.isArray(parsed.sensorKeys)
      ? parsed.sensorKeys.filter((key): key is string => typeof key === 'string' && key.length > 0)
      : getStreamKeysForWidget(parsed.name, dataKey);
    const draft: DashboardWidgetContent = {
      name: parsed.name,
      curve: parsed.curve ?? 'linear',
      style: parsed.style ?? 'line',
      dataKey,
      sensorKeys: storedSensorKeys,
      props: parsed.props ?? {},
      ...(parsed.options ? { options: parsed.options } : {}),
    };
    return {
      ...draft,
      sensorKeys: dedupeStreamKeysPreserveOrder(resolveWidgetSensorKeys(draft)),
    };
  } catch {
    return null;
  }
}

/** Reads `sensorKeys` from widget content, with a fallback for layouts saved before `sensorKeys` existed. */
export function getSensorKeysFromWidgetContent(content: string | undefined): string[] {
  return parseDashboardWidgetContent(content)?.sensorKeys ?? [];
}

/** Union of all `sensorKeys` across widgets (deduped; order follows dashboard layout, first-seen wins). */
export function collectUnionSensorKeysFromLayout(widgets: GridStackWidget[]): string[] {
  let keys: string[] = [];
  for (const widget of widgets) {
    keys = mergeStreamKeys(keys, getSensorKeysFromWidgetContent(widget.content));
  }
  return keys;
}

/**
 * Merges project layout with GridStack meta widgets (meta wins per id).
 * Use in live mode so stream ingest matches toolbar/widget edits before Redux layout catches up.
 */
export function mergeDashboardWidgetLayouts(projectLayout: GridStackWidget[], metaLayout: GridStackWidget[]): GridStackWidget[] {
  if (!metaLayout.length) return projectLayout;
  const byId = new Map<string, GridStackWidget>();
  for (const widget of projectLayout) {
    if (widget.id) byId.set(widget.id, widget);
  }
  for (const widget of metaLayout) {
    if (widget.id) byId.set(widget.id, widget);
  }
  return Array.from(byId.values());
}

/** Motor/mechanism stream keys referenced by widgets on the dashboard layout. */
export function collectMotorStreamKeysFromLayout(widgets: GridStackWidget[]): string[] {
  return collectUnionSensorKeysFromLayout(widgets).filter(isMotorStreamSensorKey);
}

/** Electronic sensor stream keys (`beam.port*`, `distance.port*`, etc.) on the dashboard layout. */
export function collectElectronicSensorStreamKeysFromLayout(widgets: GridStackWidget[]): string[] {
  return collectUnionSensorKeysFromLayout(widgets).filter(isElectronicSensorStreamSensorKey);
}

/** Other electronics stream keys (`bit_adapter.port*`, etc.) on the dashboard layout. */
export function collectOtherElectronicsStreamKeysFromLayout(widgets: GridStackWidget[]): string[] {
  return collectUnionSensorKeysFromLayout(widgets).filter(isOtherElectronicsStreamSensorKey);
}

/** Stream keys ingested as single-series float graphs (custom callables). */
export function collectFloatStreamGraphKeysFromLayout(widgets: GridStackWidget[]): string[] {
  return collectCustomCallableStreamKeysFromLayout(widgets);
}

/** Stream keys for user-registered DataStreamStore callables on the dashboard layout. */
export function collectCustomCallableStreamKeysFromLayout(widgets: GridStackWidget[]): string[] {
  const keys: string[] = [];
  for (const widget of widgets) {
    const content = parseDashboardWidgetContent(widget.content);
    if (content?.name === CUSTOM_CALLABLE_WIDGET_TYPE) {
      for (const key of content.sensorKeys) {
        if (key && !keys.includes(key)) keys.push(key);
      }
    }
  }
  return keys;
}

/**
 * Custom callable display labels for CSV export and similar UI: stream parameters from the
 * active layout plus any additional registrations found in project code.
 */
export function buildCustomCallableDisplayNameByParameterFromLayout(
  widgets: GridStackWidget[],
  registeredParametersFromCode: ReadonlySet<string>
): ReadonlyMap<string, string> {
  const labels = new Map<string, string>();
  const seen = new Set<string>();

  for (const widget of widgets) {
    const content = parseDashboardWidgetContent(widget.content);
    if (content?.name !== CUSTOM_CALLABLE_WIDGET_TYPE) continue;
    const parameter = content.dataKey;
    if (!parameter || seen.has(parameter)) continue;
    seen.add(parameter);
    labels.set(parameter, resolveCustomCallableDisplayName(parameter));
  }

  for (const parameter of registeredParametersFromCode) {
    if (seen.has(parameter)) continue;
    labels.set(parameter, resolveCustomCallableDisplayName(parameter));
  }

  return labels;
}

/**
 * Check if a widget can be added without exceeding the stream item limit.
 * Uses the union of `sensorKeys` on existing widgets plus the new widget's keys.
 */
export function canAddWidget(currentWidgets: GridStackWidget[], keysToAdd: string[]): { canAdd: boolean } {
  if (keysToAdd.length === 0) return { canAdd: true };

  const projectedKeys = mergeStreamKeys(collectUnionSensorKeysFromLayout(currentWidgets), keysToAdd);

  if (projectedKeys.length > MAX_SENSOR_STREAMS) {
    return { canAdd: false };
  }

  return { canAdd: true };
}

/**
 * Whether program code matches the expected dashboard streaming setup.
 * An existing `DataStreamStore.set_streaming_interval(...)` value is accepted as-is so users can tune it manually.
 * Explicit monitor registration is required independently of RobotBase startup, but must still be
 * placed where it executes before that startup; see `isExecutableUpdateMonitorDataCallPlacement`.
 */
export function isStreamingSetupInSync(code: string, keys: string[]): boolean {
  if (keys.length === 0) {
    return (
      !hasSetRequestListCall(code) &&
      !hasSetStreamingIntervalCall(code) &&
      !hasSpheroUtilImport(code) &&
      findDashboardManagedUpdateMonitorDataCallRanges(code).length === 0
    );
  }

  return isStreamDefinitionInSync(code, keys) && hasExecutableDashboardUpdateMonitorDataCall(code);
}

function isStreamDefinitionInSync(code: string, keys: string[]): boolean {
  const codeKeys = parseDataStreamRequestList(code) ?? [];
  return stableKeysSignature(keys) === stableKeysSignature(codeKeys) && hasSetStreamingIntervalCall(code);
}

/**
 * Whether stream keys, interval, and `set_request_list` formatting match the dashboard layout.
 *
 * Key membership is compared set-wise via `isStreamingSetupInSync`. When the key set already
 * matches, this also requires Ruff-style formatting (`isSetRequestListFormattingNormalized`).
 *
 * `useSyncDataStreamKeysFromLayout` treats a matching key set with stale one-line formatting as
 * out of sync, so the first live sync for a layout signature may dispatch a formatting-only
 * program update even when stream keys did not change.
 */
export function isDashboardProgramCodeInSync(code: string, widgets: GridStackWidget[]): boolean {
  const keys = collectUnionSensorKeysFromLayout(widgets);
  if (!isStreamingSetupInSync(code, keys)) return false;
  if (keys.length === 0) return true;
  return isSetRequestListFormattingNormalized(code, keys);
}

function getParsedRequestListKeysMatchingLayout(code: string, layoutKeys: string[]): string[] | null {
  const codeKeys = parseDataStreamRequestList(code);
  if (!codeKeys || stableKeysSignature(codeKeys) !== stableKeysSignature(layoutKeys)) {
    return null;
  }
  return codeKeys;
}

/**
 * Rebuilds dashboard streaming setup in program code: request list, interval,
 * and explicit monitor registration.
 */
export function rebuildDashboardProgramCodeFromLayout(code: string, widgets: GridStackWidget[], intervalMs = DEFAULT_STREAMING_INTERVAL_MS): string {
  return rebuildDataStreamRequestListInCode(code, widgets, intervalMs);
}

/**
 * Rebuilds dashboard streaming setup in program code: request list, interval,
 * and explicit monitor registration. Removes generated streaming setup when no widgets require stream data.
 */
export function rebuildDataStreamRequestListInCode(code: string, widgets: GridStackWidget[], intervalMs = DEFAULT_STREAMING_INTERVAL_MS): string {
  const keys = collectUnionSensorKeysFromLayout(widgets);

  if (keys.length === 0) {
    return removeStreamingSetupFromCode(code);
  }

  const requestLine = formatDataStreamRequestListLine(keys);
  const intervalLine = formatSetStreamingIntervalLine(intervalMs);

  if (isStreamDefinitionInSync(code, keys)) {
    let next = code;
    if (!isSetRequestListFormattingNormalized(next, keys)) {
      const codeKeys = getParsedRequestListKeysMatchingLayout(next, keys) ?? keys;
      next = replaceSetRequestListCall(next, codeKeys);
    }
    return ensureUpdateMonitorDataCall(next);
  }

  let next = code;

  if (hasSetRequestListCall(next)) {
    next = replaceSetRequestListCall(next, keys);
  } else {
    next = insertStreamingBlockBeforeRobotStart(next, requestLine, intervalLine);
    return ensureUpdateMonitorDataCall(next);
  }

  next = insertSetStreamingIntervalLineIfMissing(next, intervalLine);
  return ensureUpdateMonitorDataCall(next);
}

/**
 * Locates every `DataStreamStore.set_request_list([...])` in source order, including multiline list
 * literals. The legacy `DataStreamStore.get_instance().set_request_list([...])` form is matched too,
 * so hand-authored and previously generated programs keep syncing.
 * Uses the same multiline call-arg scanner as `driveModeAutonomousActionUtils.extractCallArgsText`.
 *
 * **Supported shape:** the receiver must be the whole line prefix, so the statement starts at the line
 * start and `span.start` can safely own the line. That deliberately skips a commented-out call and a
 * call sharing a line with another statement, either of which a rewrite would otherwise clobber.
 * The opening `(` must be on the same line; splitting the call across lines is not matched.
 *
 * Matches are additionally kept only where `collectModuleFlowStatements` reports a statement start.
 * The shape rules above already exclude dead text on their own; this keeps `startLineIndex` pointing
 * at a real statement header, which is what {@link resolveExpectedStreamWireOrder} compares against
 * when deciding whether a registration sits in unconditional module flow.
 */
function findAllSetRequestListCallSpans(code: string): SetRequestListCallSpan[] {
  const lines = splitCodeLines(code);
  const spans: SetRequestListCallSpan[] = [];
  const statementStartLines = new Set(collectModuleFlowStatements(code, { includeDefSuites: true }).map((statement) => statement.startLineIndex));

  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    const line = lines[lineIndex] ?? '';
    if (!statementStartLines.has(lineIndex)) continue;
    const markerIdx = line.indexOf(SET_REQUEST_LIST_MARKER);
    if (markerIdx === -1) continue;

    const linePrefix = line.slice(0, markerIdx);
    if (!SET_REQUEST_LIST_RECEIVER_PREFIX_RE.test(linePrefix)) continue;

    // A call inside a docstring is documentation, not the program's request list. The prefix test
    // cannot see it — inside `"""..."""` the line looks exactly like a real statement — and this
    // scanner returns the FIRST match, so an example above the live call would be rewritten in its
    // place, leaving the executable one stale.
    const markerGlobal = offsetAtCodeLineColumn(code, lineIndex, markerIdx);
    if (isOffsetInPythonStringOrLineComment(code, markerGlobal)) continue;

    // The `(` must belong to this method: only horizontal whitespace may separate them. Searching
    // for the next `(` anywhere on the line managed two things it should not — a longer method
    // sharing the prefix (`set_request_list_backup(...)`, or any Unicode continuation such as
    // `set_request_listé(...)`), and a bare method reference whose line happens to call something
    // else (`DataStreamStore.set_request_list and print([...])`). Both would have been rewritten
    // into the dashboard's own call, replacing code the user wrote. Requiring adjacency covers every
    // such case without this scanner having to model Python's identifier grammar.
    const afterMarkerIdx = markerIdx + SET_REQUEST_LIST_MARKER.length;
    const openParenMatch = /^[ \t]*\(/.exec(line.slice(afterMarkerIdx));
    if (!openParenMatch) continue;

    const parenIdx = afterMarkerIdx + openParenMatch[0].length - 1;
    if (parenIdx === -1) continue;

    const extracted = extractCallArgsText(lines, lineIndex, parenIdx, { ignoreLineComments: true });
    if (!extracted) continue;

    const openOffset = offsetAtCodeLineColumn(code, lineIndex, parenIdx);
    const argsStart = skipWhitespaceInCode(code, openOffset + 1);
    if (code[argsStart] !== '[') continue;

    const listCloseIdx = findMatchingDelimiterIndex(code, argsStart, '[', ']', { ignoreLineComments: true });
    if (listCloseIdx === -1) continue;

    const closeParenIdx = findMatchingDelimiterIndex(code, openOffset, '(', ')', { ignoreLineComments: true });
    if (closeParenIdx === -1) continue;

    // The rewrite replaces through the end of the statement's line, so anything but a trailing
    // comment after the call — a `; print(...)` sibling above all — would be orphaned onto its own
    // line as a syntax error. Skip those statements instead of corrupting them.
    const trailingLineBreak = code.indexOf('\n', closeParenIdx + 1);
    const trailingText = code.slice(closeParenIdx + 1, trailingLineBreak === -1 ? code.length : trailingLineBreak);
    if (!SET_REQUEST_LIST_TRAILING_RE.test(trailingText)) continue;

    const dataStoreIdx = code.lastIndexOf('DataStreamStore', markerGlobal);
    const stmtLineStart = dataStoreIdx >= 0 ? code.lastIndexOf('\n', dataStoreIdx) + 1 : offsetAtCodeLineColumn(code, lineIndex, 0);

    spans.push({
      start: stmtLineStart,
      end: consumeThroughLineBreak(code, closeParenIdx + 1),
      listInnerStart: argsStart + 1,
      listInnerEnd: listCloseIdx,
      calleeStart: dataStoreIdx >= 0 ? dataStoreIdx : stmtLineStart,
      startLineIndex: lineIndex,
    });
  }

  return spans;
}

function findSetRequestListCallSpan(code: string): SetRequestListCallSpan | null {
  return findAllSetRequestListCallSpans(code)[0] ?? null;
}

function hasSetRequestListCall(code: string): boolean {
  return findSetRequestListCallSpan(code) !== null;
}

function removeStreamingSetupFromCode(code: string): string {
  let next = removeUpdateMonitorDataCalls(code, findDashboardManagedUpdateMonitorDataCallRanges(code));
  next = removeSetStreamingIntervalCall(next);
  next = removeSetRequestListCall(next);
  next = removeDataStreamingRegionBlock(next);
  next = removeSpheroUtilImport(next);
  return next.replace(/\n{3,}/g, '\n\n');
}

function removeUpdateMonitorDataCalls(code: string, ranges: ReturnType<typeof findUpdateMonitorDataCallRanges>): string {
  return ranges
    .slice()
    .sort((left, right) => right.start - left.start)
    .reduce((next, range) => next.slice(0, range.start) + next.slice(range.end), code)
    .replace(/\n{3,}/g, '\n\n');
}

function hasSpheroUtilImport(code: string): boolean {
  return splitCodeLines(code).some((line) => /^\s*from\s+sphero_util\s+import\s+\*/.test(line));
}

function removeSpheroUtilImport(code: string): string {
  const lines = splitCodeLines(code).filter((line) => !/^\s*from\s+sphero_util\s+import\s+\*/.test(line));
  const next = lines.join('\n');
  return code.endsWith('\n') && next.length > 0 ? `${next}\n` : next;
}

function hasSetStreamingIntervalCall(code: string): boolean {
  return parseStreamingIntervalMs(code) !== null;
}

/** Reads the interval argument from `DataStreamStore.set_streaming_interval(...)`, if present. */
/**
 * The one interval statement the program actually executes, skipping documented examples.
 *
 * Presence and span resolution share this lookup deliberately: when they scanned separately, the
 * presence check read a docstring interval while the span lookup ignored it, so the dashboard
 * concluded an interval already existed and never inserted an executable one.
 */
function findExecutableSetStreamingIntervalLine(code: string): { line: string; intervalMs: number; start: number } | null {
  const lines = splitCodeLines(code);
  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    const line = lines[lineIndex] ?? '';
    const match = line.match(SET_STREAMING_INTERVAL_LINE_RE);
    if (!match) continue;
    const start = offsetAtCodeLineColumn(code, lineIndex, 0);
    if (isOffsetInPythonStringOrLineComment(code, start)) continue;
    return { line, intervalMs: Number(match[1]), start };
  }
  return null;
}

export function parseStreamingIntervalMs(code: string): number | null {
  return findExecutableSetStreamingIntervalLine(code)?.intervalMs ?? null;
}

function formatSetStreamingIntervalLine(intervalMs: number): string {
  return `DataStreamStore.set_streaming_interval(${intervalMs})`;
}

function findSetStreamingIntervalLineSpan(code: string): { start: number; end: number } | null {
  const found = findExecutableSetStreamingIntervalLine(code);
  if (!found) return null;
  return { start: found.start, end: consumeThroughLineBreak(code, found.start + found.line.length) };
}

function removeSetStreamingIntervalCall(code: string): string {
  const span = findSetStreamingIntervalLineSpan(code);
  if (!span) return code;
  return code.slice(0, span.start) + code.slice(span.end).replace(/\n{3,}/g, '\n\n');
}

/** Inserts `DataStreamStore.set_streaming_interval(...)` only when absent; never overwrites a user-edited value. */
function indentPythonStatement(indent: string, statement: string): string {
  return statement
    .split('\n')
    .map((line) => `${indent}${line}`)
    .join('\n');
}

function getSetRequestListStatementIndent(code: string, span: SetRequestListCallSpan): string {
  return getIndentAtCodeIndex(code, span.calleeStart);
}

function insertSetStreamingIntervalLineIfMissing(code: string, intervalLine: string): string {
  if (hasSetStreamingIntervalCall(code)) return code;

  const requestSpan = findSetRequestListCallSpan(code);
  if (!requestSpan) return code;

  const indent = getSetRequestListStatementIndent(code, requestSpan);
  const insertText = `${indent}${intervalLine.trimStart()}\n`;
  return code.slice(0, requestSpan.end) + insertText + code.slice(requestSpan.end);
}

function ensureUpdateMonitorDataCall(code: string): string {
  if (hasExecutableDashboardUpdateMonitorDataCall(code)) return code;

  const existingRanges = findDashboardManagedUpdateMonitorDataCallRanges(code);
  const next = existingRanges.length > 0 ? removeUpdateMonitorDataCalls(code, existingRanges) : code;
  const lineBreak = next.includes('\r\n') ? '\r\n' : '\n';
  const intervalSpan = findSetStreamingIntervalLineSpan(next);
  if (intervalSpan) {
    const indent = next.slice(intervalSpan.start, intervalSpan.end).match(/^([ \t]*)/)?.[1] ?? '';
    const separator = next.slice(intervalSpan.start, intervalSpan.end).endsWith('\n') ? '' : lineBreak;
    return `${next.slice(0, intervalSpan.end)}${separator}${indent}${UPDATE_MONITOR_DATA_CALL}${lineBreak}${next.slice(intervalSpan.end)}`;
  }

  const requestSpan = findSetRequestListCallSpan(next);
  if (requestSpan) {
    const indent = getSetRequestListStatementIndent(next, requestSpan);
    const separator = next.slice(requestSpan.start, requestSpan.end).endsWith('\n') ? '' : lineBreak;
    return `${next.slice(0, requestSpan.end)}${separator}${indent}${UPDATE_MONITOR_DATA_CALL}${lineBreak}${next.slice(requestSpan.end)}`;
  }

  return next;
}

function findDashboardManagedUpdateMonitorDataCallRanges(code: string): ReturnType<typeof findUpdateMonitorDataCallRanges> {
  const region = findDataStreamingRegionSpan(code);
  const interval = findSetStreamingIntervalLineSpan(code);

  return findUpdateMonitorDataCallRanges(code).filter((range) => {
    const isModuleLevel = !/^\s/.test(range.sourceText);
    const isInsideStreamingRegion = region !== null && range.start >= region.start && range.end <= region.end;
    const immediatelyFollowsInterval = interval !== null && range.start === interval.end;
    return isModuleLevel || isInsideStreamingRegion || immediatelyFollowsInterval;
  });
}

/**
 * Whether a dashboard-owned registration sits where the framework will actually execute it.
 *
 * `RobotBase.start()` runs a blocking loop for the life of the program, so anything below it never
 * runs and dashboard streaming is never finalized. Position relative to that startup is the whole
 * contract: belonging to the generated region or trailing the managed interval does not help when
 * the block itself sits below startup, and when the block sits above startup the call already
 * satisfies this check on its own.
 *
 * Ownership is unchanged by this check: a misplaced call is still dashboard-managed, so zero-key
 * cleanup keeps removing it.
 */
function isExecutableUpdateMonitorDataCallPlacement(code: string, range: UpdateMonitorDataCallRange): boolean {
  const startLine = getEffectiveRobotStartLineInCode(code);
  return startLine === null || range.startLine < startLine;
}

/** True when exactly one dashboard-owned registration exists and it will execute before robot startup. */
function hasExecutableDashboardUpdateMonitorDataCall(code: string): boolean {
  const ranges = findDashboardManagedUpdateMonitorDataCallRanges(code);
  return ranges.length === 1 && isExecutableUpdateMonitorDataCallPlacement(code, ranges[0]);
}

function getIndentBeforeRobotStart(code: string): string {
  const startLine = getInsertLineBeforeRobotStartInCode(code);
  if (startLine === null) return '';
  const lines = splitCodeLines(code);
  const line = lines[startLine - 1] ?? '';
  const match = line.match(/^(\s*)/);
  return match?.[1] ?? '';
}

function findDataStreamingRegionSpan(code: string): { start: number; end: number; endLine: number } | null {
  const lines = splitCodeLines(code);
  const span = findRegionSpanLineIndices(lines, isDataStreamingRegionStartLine);
  if (!span) return null;

  const { startLine, endLine } = span;
  const start = offsetAtCodeLineColumn(code, startLine, 0);
  const endLineText = lines[endLine] ?? '';
  const end = consumeThroughLineBreak(code, offsetAtCodeLineColumn(code, endLine, endLineText.length));
  return { start, end, endLine };
}

function removeDataStreamingRegionBlock(code: string): string {
  const span = findDataStreamingRegionSpan(code);
  if (!span) return code;
  return code.slice(0, span.start) + code.slice(span.end).replace(/\n{3,}/g, '\n\n');
}

/**
 * Inserts request list, interval, and monitor registration before `robot.start()` (or at file end).
 * Wraps in a BPS region only on first-time insert; never re-adds region markers if the user removed them.
 */
function insertStreamingBlockBeforeRobotStart(code: string, requestLine: string, intervalLine: string): string {
  const lines = splitCodeLines(code);
  const lineBreak = code.includes('\r\n') ? '\r\n' : '\n';
  const insertLine = getInsertLineBeforeRobotStartInCode(code);
  const insertAt = insertLine !== null ? insertLine - 1 : lines.length;
  const indent = insertLine !== null ? getIndentBeforeRobotStart(code) : '';
  const contentLines = [
    ...indentPythonStatement(indent, requestLine.trimStart()).split('\n'),
    `${indent}${intervalLine.trimStart()}`,
    `${indent}${UPDATE_MONITOR_DATA_CALL}`,
  ];

  const region = findDataStreamingRegionSpan(code);
  if (region) {
    const nextLines = [...lines.slice(0, region.endLine), ...contentLines, ...lines.slice(region.endLine)];
    const next = nextLines.join(lineBreak);
    return code.endsWith('\n') || code.length === 0 ? `${next}${lineBreak}` : next;
  }

  const blockLines = [`${indent}${DATA_STREAMING_REGION_START}`, ...contentLines, `${indent}${REGION_END}`, ''];
  const nextLines = [...lines.slice(0, insertAt), ...blockLines, ...lines.slice(insertAt)];
  const next = nextLines.join(lineBreak);
  return code.endsWith('\n') || code.length === 0 ? `${next}${lineBreak}` : next;
}

function removeSetRequestListCall(code: string): string {
  const span = findSetRequestListCallSpan(code);
  if (!span) return code;
  return code.slice(0, span.start) + code.slice(span.end).replace(/\n{3,}/g, '\n\n');
}

function replaceSetRequestListCall(code: string, keys: string[]): string {
  // Key-change replacements pass layout order and drop inline list comments.
  // Formatting-only replacements pass parsed code order.
  const span = findSetRequestListCallSpan(code);
  if (!span) return code;
  const indent = getSetRequestListStatementIndent(code, span);
  const nextLine = formatDataStreamRequestListLine(keys);
  // span.end consumes the statement's trailing newline; restore it so the next line is not concatenated.
  return code.slice(0, span.start) + indentPythonStatement(indent, nextLine.trimStart()) + '\n' + code.slice(span.end);
}

/** Reads stream item names from an existing `set_request_list([...])` call, if present. */
export function parseDataStreamRequestList(code: string): string[] | null {
  const span = findSetRequestListCallSpan(code);
  if (!span) return null;
  const inner = code.slice(span.listInnerStart, span.listInnerEnd);
  return parseDoubleQuotedStringLiterals(stripPythonLineCommentsOutsideStrings(inner));
}

export type PythonStreamDefinition = { name: string; micropythonType: string };

/**
 * Zero-based lines whose statement runs exactly once, in source order, when the module executes.
 *
 * Only unindented module-level statements qualify. Anything in an `if`/`for`/`while`/`try` suite,
 * a `def` body, or a `class` body either runs conditionally, repeats, or defers to a call site,
 * and this lexical scan cannot tell which.
 */
function collectLinearModuleStatementLines(code: string): Set<number> {
  return new Set(
    collectModuleFlowStatements(code, { includeDefSuites: true })
      .filter((statement) => statement.indentColumn === 0 && !statement.insideDefOrClass)
      .map((statement) => statement.startLineIndex)
  );
}

/**
 * Expected wire order across every registration pass the source performs.
 *
 * The robot registers the request list as it stands at each `update_monitor_data()` call, and a
 * program may set a different list before calling again, so the wire is the concatenation of the
 * list active at each call rather than one list repeated. Returns `null` when no registration call
 * is visible, leaving callers on the single-`set_request_list` path.
 *
 * Deriving a sequence requires knowing that each call runs, once, in source order, so any
 * registration statement under `if`, `for`, `while`, `try`, a `def`, or a `class` makes the whole
 * sequence unprovable and returns `null` as well. A branch that never runs would otherwise
 * contribute positions the wire does not have, and shifting the remaining names by even one slot
 * can still total the frame's byte length, which mislabels every value with nothing to catch it.
 */
export function resolveExpectedStreamWireOrder(code: string): string[] | null {
  const updateCalls = findUpdateMonitorDataCallRanges(code);
  if (!updateCalls.length) return null;

  const linearStatementLines = collectLinearModuleStatementLines(code);
  if (updateCalls.some((call) => !linearStatementLines.has(call.startLine - 1))) return null;

  const requestListSpans = findAllSetRequestListCallSpans(code);
  if (requestListSpans.some((span) => !linearStatementLines.has(span.startLineIndex))) return null;

  const requestLists = requestListSpans.map((span) => ({
    start: span.start,
    keys: parseDoubleQuotedStringLiterals(stripPythonLineCommentsOutsideStrings(code.slice(span.listInnerStart, span.listInnerEnd))),
  }));
  if (!requestLists.length) return null;

  const wireOrder: string[] = [];
  for (const call of updateCalls) {
    // The list in effect at this call is the last one assigned before it.
    let active: string[] | null = null;
    for (const list of requestLists) {
      if (list.start >= call.start) break;
      active = list.keys;
    }
    if (!active) continue;
    wireOrder.push(...active);
  }

  return wireOrder.length ? wireOrder : null;
}

/**
 * The one request-list order the program registers, when it only ever registers that order.
 *
 * A pass registers whatever list is current, so when the source sets exactly one order the wire is
 * that order repeated, whatever made it repeat. That holds without knowing how many passes ran, so
 * it covers loops, helpers, and branches that {@link resolveExpectedStreamWireOrder} refuses. Any
 * second, different order brings back the question of which order applied to which pass, so this
 * returns `null` and the strict path decides.
 *
 * Indented and deferred calls count here: a nested `set_request_list` still proves another order
 * exists, and ignoring it is what would make repetition look safe when it is not.
 */
export function resolveRepeatableStreamWireUnit(code: string): string[] | null {
  if (!findUpdateMonitorDataCallRanges(code).length) return null;

  const lists = findAllSetRequestListCallSpans(code).map((span) =>
    parseDoubleQuotedStringLiterals(stripPythonLineCommentsOutsideStrings(code.slice(span.listInnerStart, span.listInnerEnd)))
  );
  const [first] = lists;
  if (!first?.length) return null;

  const signature = JSON.stringify(first);
  return lists.every((keys) => JSON.stringify(keys) === signature) ? [...first] : null;
}

/**
 * Repeats `unit` to cover `totalSlots`, or `null` when the slots are not a whole number of repeats.
 *
 * A partial repeat means the robot registered something this unit does not describe, so there is
 * nothing to tile against.
 */
export function tileStreamWireUnitToSlotCount(unit: readonly string[] | null | undefined, totalSlots: number): string[] | null {
  if (!unit?.length || totalSlots <= 0 || totalSlots % unit.length !== 0) return null;
  return Array.from({ length: totalSlots }, (_, index) => unit[index % unit.length] as string);
}

/**
 * Reorders FW-reported stream definitions to match `set_request_list` wire order.
 * Micropython emits payload values in request-list order; parser indexes definitions
 * by position, so notification order must match code order (BPS-687).
 *
 * `expectedWireOrder` may repeat a name when the program registers the list more than once. Pass
 * the order from `resolveExpectedStreamWireOrder` so repeats carry each pass's real order.
 *
 * Returns `null` when the robot registered a name more often than the expected order accounts for,
 * meaning the wire order cannot be established from source. Callers must fail closed on `null`
 * rather than stream definitions whose positions are a guess.
 */
export function reorderPythonStreamDefinitionsToRequestList(
  definitions: ReadonlyArray<PythonStreamDefinition>,
  expectedWireOrder: readonly string[] | null | undefined
): PythonStreamDefinition[] | null {
  if (!definitions.length) return [];
  const firmwareOrder = () => definitions.map((def) => ({ name: def.name, micropythonType: def.micropythonType }));
  if (!expectedWireOrder?.length) return firmwareOrder();

  const remainingByName = new Map<string, PythonStreamDefinition[]>();
  for (const def of definitions) {
    const name = String(def?.name ?? '').trim();
    const slot = { name, micropythonType: def.micropythonType };
    const bucket = remainingByName.get(name);
    if (bucket) bucket.push(slot);
    else remainingByName.set(name, [slot]);
  }

  const expectedCounts = new Map<string, number>();
  for (const key of expectedWireOrder) {
    const trimmed = String(key ?? '').trim();
    if (!trimmed) continue;
    expectedCounts.set(trimmed, (expectedCounts.get(trimmed) ?? 0) + 1);
  }

  // The robot registered a name more times than the source accounts for, so a registration ran that
  // the source cannot count: a loop or a helper, say. The extra slots have no derivable position,
  // and firmware's own grouping is not the wire order either (BPS-687), so every available ordering
  // here is a guess. A guess whose byte widths happen to total the frame length is indistinguishable
  // from a correct one to the positional parser, so report failure and let the caller stop.
  for (const [name, slots] of remainingByName) {
    const expected = expectedCounts.get(name) ?? 0;
    if (expected > 0 && slots.length > expected) return null;
  }

  const ordered: PythonStreamDefinition[] = [];
  for (const key of expectedWireOrder) {
    const trimmed = String(key ?? '').trim();
    if (!trimmed) continue;
    const bucket = remainingByName.get(trimmed);
    if (!bucket?.length) continue;
    ordered.push(bucket.shift() as PythonStreamDefinition);
  }

  // Names the wire order never mentions (and unnamed slots) keep firmware order at the end.
  for (const def of definitions) {
    const bucket = remainingByName.get(String(def?.name ?? '').trim());
    if (!bucket?.length) continue;
    ordered.push(bucket.shift() as PythonStreamDefinition);
  }

  return ordered;
}

/** Receiver the dashboard generates; the legacy accessor is still accepted in existing programs. */
const DATA_STREAM_STORE_RECEIVER = 'DataStreamStore.';
const LEGACY_DATA_STREAM_STORE_RECEIVER = 'DataStreamStore.get_instance().';

function formatDataStreamRequestListLine(keys: string[], receiver: string = DATA_STREAM_STORE_RECEIVER): string {
  const items = keys.map((key) => `"${escapePythonStringLiteral(key)}"`).join(', ');
  return formatGeneratedPythonCall(`${receiver}set_request_list([${items}])`);
}

function getSetRequestListAnnotationScanText(code: string, span: SetRequestListCallSpan): string {
  const statement = code.slice(span.start, span.end).replace(/\r?\n$/, '');
  const lineBreak = code.indexOf('\n', span.end);
  const lineEnd = lineBreak === -1 ? code.length : lineBreak;
  return statement + code.slice(span.end, lineEnd);
}

function isSetRequestListFormattingNormalized(code: string, keys: string[]): boolean {
  const span = findSetRequestListCallSpan(code);
  if (!span) return false;

  if (containsPythonLineCommentOutsideStrings(getSetRequestListAnnotationScanText(code, span))) {
    return true;
  }

  const codeKeys = getParsedRequestListKeysMatchingLayout(code, keys);
  if (!codeKeys) return false;

  const indent = getSetRequestListStatementIndent(code, span);
  const statement = code.slice(span.start, span.end).replace(/\r?\n$/, '');
  // A well-formatted legacy `get_instance()` call counts as normalized, so a program whose keys and
  // formatting already match is never rewritten just because the dashboard opened. The receiver is
  // upgraded only on a statement that had to be rewritten anyway — a key change, or formatting that
  // was already stale on the previous receiver.
  return [DATA_STREAM_STORE_RECEIVER, LEGACY_DATA_STREAM_STORE_RECEIVER].some(
    (receiver) => statement === indentPythonStatement(indent, formatDataStreamRequestListLine(codeKeys, receiver).trimStart())
  );
}

export function mergeStreamKeys(existing: string[], keysToAdd: string[]): string[] {
  const merged = [...existing];
  for (const key of keysToAdd) {
    if (!merged.includes(key)) merged.push(key);
  }
  return merged;
}

/** Replaces stream keys in an existing `set_request_list` call; no-op when the call is absent. */
export function replaceDataStreamRequestListInCode(code: string, keys: string[]): string {
  if (!hasSetRequestListCall(code)) return code;
  return replaceSetRequestListCall(code, keys);
}

/** Preserves first-seen order while removing duplicate stream keys. */
export function dedupeStreamKeysPreserveOrder(keys: readonly string[]): string[] {
  return mergeStreamKeys([], [...keys]);
}

/** Drive-unit ports referenced by a saved dashboard widget (motors, sensors, other electronics). */
export function historicLayoutWidgetPorts(content: DashboardWidgetContent): number[] {
  const opts = content.options;
  if (opts?.selectedMotorPort != null) return [opts.selectedMotorPort];
  if (opts?.selectedMotorPorts?.length) return opts.selectedMotorPorts;
  if (opts?.selectedSensorPorts?.length) return opts.selectedSensorPorts;
  if (opts?.selectedOtherPorts?.length) return opts.selectedOtherPorts;

  const fromOtherKeys = otherPortsFromStreamKeys(content.sensorKeys, content.dataKey);
  if (fromOtherKeys.length) return fromOtherKeys;

  const motorPorts = motorPortsFromStreamKeys(content.sensorKeys, content.dataKey);
  if (motorPorts.length) return motorPorts;

  const ports = [content.dataKey, ...content.sensorKeys]
    .map((key) => streamPortFromSensorKey(key))
    .filter((port): port is number => port !== null && Number.isFinite(port));
  return [...new Set(ports)].sort((a, b) => a - b);
}

function snapshotPortDisplayLabels(
  existing: Record<string, string> | undefined,
  ports: readonly number[],
  labelByPort: ReadonlyMap<number, string>
): Record<string, string> {
  const portLabels: Record<string, string> = { ...(existing ?? {}) };
  for (const port of ports) {
    const displayLabel = labelByPort.get(port);
    if (displayLabel && portLabels[String(port)] !== displayLabel) {
      portLabels[String(port)] = displayLabel;
    }
  }
  return portLabels;
}

/** Saved per-port labels from widget options (supports pre-rename snapshot field). */
export function portDisplayLabelsRecordFromWidgetOptions(options?: DashboardWidgetContent['options']): Record<string, string> | undefined {
  return options?.portDisplayLabelsByPort ?? options?.motorPortDisplayLabelsByPort;
}

/**
 * Fills missing per-port variable and display labels on the dashboard layout before saving a run snapshot.
 * Historic CSV export and chart series buttons read these values instead of live drive-unit occupancy.
 */
export function enrichDashboardLayoutVariableNamesForRunSnapshot(
  layout: GridStackWidget[],
  driveUnitPorts: DriveUnitPortOccupancy[],
  projectCode: string
): GridStackWidget[] {
  if (!layout.length || !projectCode.trim()) return layout;

  const motorVariableNameByPort = buildMotorVariableNameByPort(driveUnitPorts, projectCode);
  const motorPortLabelByPort = buildMotorChartSeriesLabelByPort(driveUnitPorts, projectCode);
  const mechanismVariableNameByPort = buildMechanismVariableNameByPort(driveUnitPorts, projectCode);
  const mechanismPortLabelByPort = buildMechanismDrawerPortLabelByPort(driveUnitPorts, projectCode);
  const sensorVariableNameByPort = new Map<number, string>();
  for (const sensorClassName of getConfiguredSensorClassNames(driveUnitPorts, projectCode)) {
    for (const [port, variableName] of buildSensorVariableNameByPort(driveUnitPorts, projectCode, sensorClassName)) {
      sensorVariableNameByPort.set(port, variableName);
    }
  }
  const otherElectronicsVariableNameByPort = buildOtherElectronicsVariableNameByPort(driveUnitPorts, projectCode);

  return layout.map((widget) => {
    if (!widget.content) return widget;
    const content = parseDashboardWidgetContent(widget.content);
    if (!content) return widget;

    const opts = content.options ?? {};
    let nextOptions = opts;
    let changed = false;

    const applyPatch = (patch: NonNullable<DashboardWidgetContent['options']>) => {
      nextOptions = { ...nextOptions, ...patch };
      changed = true;
    };

    const ports = historicLayoutWidgetPorts(content);

    if (content.name === MOTOR_MULTI_WIDGET_TYPE && opts.selectedMotorPort != null) {
      const port = opts.selectedMotorPort;
      const prefix = parseMotorMultiWidgetDataKey(content.dataKey)?.prefix;
      const isMechanism = prefix === 'mechanism' || Boolean(opts.mechanismClassName);
      if (!opts.motorVariableName?.trim()) {
        const variableName = isMechanism ? mechanismVariableNameByPort.get(port) : motorVariableNameByPort.get(port);
        if (variableName) applyPatch({ motorVariableName: variableName });
      }
      if (!isMechanism && !opts.motorPortDisplayLabel?.trim()) {
        const displayLabel = motorPortLabelByPort.get(port);
        if (displayLabel) applyPatch({ motorPortDisplayLabel: displayLabel });
      }
    }

    if (
      content.name === MOTOR_WIDGET_TYPE ||
      content.name === MECHANISM_WIDGET_TYPE ||
      (content.name === BOOLEAN_WIDGET_TYPE && content.sensorKeys.some(isMotorStreamSensorKey))
    ) {
      const isMechanismWidget = content.name === MECHANISM_WIDGET_TYPE || content.sensorKeys.some((key) => key.startsWith('mechanism.'));
      const portLabels = snapshotPortDisplayLabels(
        portDisplayLabelsRecordFromWidgetOptions(opts),
        ports,
        isMechanismWidget ? mechanismPortLabelByPort : motorPortLabelByPort
      );
      if (Object.keys(portLabels).length) {
        applyPatch({ portDisplayLabelsByPort: portLabels });
      }
      if (ports.length === 1 && !opts.motorVariableName?.trim()) {
        const port = ports[0];
        const variableName = isMechanismWidget ? mechanismVariableNameByPort.get(port) : motorVariableNameByPort.get(port);
        if (variableName) applyPatch({ motorVariableName: variableName });
      }
      if (!isMechanismWidget && ports.length === 1 && !opts.motorPortDisplayLabel?.trim()) {
        const displayLabel = motorPortLabelByPort.get(ports[0]);
        if (displayLabel) applyPatch({ motorPortDisplayLabel: displayLabel });
      }
    }

    if (content.name === SENSOR_WIDGET_TYPE || (content.name === BOOLEAN_WIDGET_TYPE && content.sensorKeys.some(isElectronicSensorStreamSensorKey))) {
      const sensorClassName = opts.sensorClassName ?? sensorClassNameFromStreamKey(content.dataKey);
      if (sensorClassName) {
        const sensorPortLabelByPort = buildSensorChartSeriesLabelByPort(driveUnitPorts, projectCode, sensorClassName);
        const portLabels = snapshotPortDisplayLabels(portDisplayLabelsRecordFromWidgetOptions(opts), ports, sensorPortLabelByPort);
        if (Object.keys(portLabels).length) {
          applyPatch({ portDisplayLabelsByPort: portLabels });
        }
      }
      if (ports.length === 1 && !opts.sensorVariableName?.trim()) {
        const variableName = sensorVariableNameByPort.get(ports[0]);
        if (variableName) applyPatch({ sensorVariableName: variableName });
      }
    }

    if (content.name === OTHER_WIDGET_TYPE || content.sensorKeys.some(isOtherElectronicsStreamSensorKey)) {
      const otherClassName = opts.otherClassName ?? otherClassNameFromStreamKey(content.dataKey);
      if (otherClassName) {
        const otherPortLabelByPort = buildOtherElectronicsChartSeriesLabelByPort(driveUnitPorts, projectCode, otherClassName);
        const portLabels = snapshotPortDisplayLabels(portDisplayLabelsRecordFromWidgetOptions(opts), ports, otherPortLabelByPort);
        if (Object.keys(portLabels).length) {
          applyPatch({ portDisplayLabelsByPort: portLabels });
        }
      }
      if (ports.length === 1 && !opts.otherVariableName?.trim()) {
        const variableName = otherElectronicsVariableNameByPort.get(ports[0]);
        if (variableName) applyPatch({ otherVariableName: variableName });
      }
    }

    if (!changed) return widget;
    return {
      ...widget,
      content: JSON.stringify({
        ...content,
        options: nextOptions,
      }),
    };
  });
}
